import "./lessonsStandards.css";
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch } from "@fortawesome/free-solid-svg-icons";
import { constants } from "utils";
import { trackClickedButton } from "utils/analytics";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  assignLesson,
  useCurriculumCourses,
  useCurriculumUnits,
  useLessonDetail,
  useLessonQuestions,
  useNoQuestionTracks,
  usePrefetchCurriculumUnits,
} from "hooks/useCurriculum/useCurriculum";
import { useEducatorAccount } from "hooks/useEducatorAccount/useEducatorAccount";
import { useTeacherGradeDefault } from "hooks/useTeacherGradeDefault/useTeacherGradeDefault";
import { LessonSidebarTree } from "./LessonSidebarTree";
import { LessonDetailPanel } from "./LessonDetailPanel";
import { EMPTY_QUESTION_FILTER } from "./QuestionFilterPanel";
import { toTrackQuestionFilters } from "./questionFilters";
import { useQuestionFacets } from "hooks/useQuestionFacets/useQuestionFacets";
import { AssignGroupsDistributionModal } from "components/pages/Educator/Dashboard/AssignGroupsDistributionModal";
import { useStandards, usePreloadStandards } from "hooks/useStandards/useStandards";
import { StandardsFallback } from "components/shared/StandardsFallback/StandardsFallback";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { useSemanticLessonSearch } from "hooks/useSemanticLessonSearch";
import {
  getGradeLabel,
  defaultTeacherGrade,
  buildingTowardOrTier1,
  lessonPrereqCodes,
  courseMatchesGrade,
  gradeHasVisibleStandards,
  isAssignableTarget,
  findSelectionByCode,
  standardsFrameworkId,
  sortCoursesBySequence,
} from "utils/standards";
import { logError, LogCategory } from "utils/sentryLogger";
import { StandardNavigationTree } from "components/pages/GroupCodeStandardSearch/StandardNavigationTree";
import { StandardDetailView } from "components/pages/GroupCodeStandardSearch/StandardDetailView";
import { SearchResultsView } from "components/pages/GroupCodeStandardSearch/SearchResultsView";
import { LessonSearchResultsView } from "components/pages/LessonsStandards/LessonSearchResultsView";
import { SIDEBAR_BUTTONS, SIDEBAR_MODE_LABELS, sidebarGradeValue, useSidebarTracking } from "hooks/useSidebarTracking";
import { withPanelViewMemory } from "hooks/usePageViewTracker";
import logo from "assets/OKO_Core_Logotype2.svg";
import arrow_left_circle from "assets/arrow-left-circle.svg";

// Lucide circle-arrow-up, from the Figma asset.
const BackToTopIcon = () => (
  <svg width="24" height="24" viewBox="0 0 21 21" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.5 10.5L10.5 6.5L14.5 10.5M10.5 6.5V14.5M20.5 10.5C20.5 16.0228 16.0228 20.5 10.5 20.5C4.97715 20.5 0.5 16.0228 0.5 10.5C0.5 4.97715 4.97715 0.5 10.5 0.5C16.0228 0.5 20.5 4.97715 20.5 10.5Z"
      stroke="#002E19"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Copy for the core question bank. LessonDetailPanel keeps its own defaults for
// the edit view (ViewChangeLesson), which has its own chrome.
const BANK_TITLE = "Preview Question bank";
const BANK_SUBTITLE =
  'Explore the available questions for the core lesson using the filters below. After you select "Create Lesson Groups" you can customize the question set for each group or let OKO randomly assign questions from this bank.';

const LessonsStandardsPage = () => {
  const history = useHistory();
  const trackSidebar = useSidebarTracking();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const classroomId = urlParams.get("classroomId");
  const initialCourseId = urlParams.get("courseId");
  const initialLessonId = urlParams.get("lessonId");
  // Defaulting to the class's last-worked bundle (see LessonGroupsList): `mode`
  // opens Standard vs Lesson; `grade` opens that grade (its course in lesson
  // mode, or the standard-mode grade dropdown). Both absent → today's defaults.
  const initialMode = urlParams.get("mode");
  const initialGrade = urlParams.get("grade");
  // `standard` opens Standard mode on that standard/cluster/domain, the way
  // `lessonId` opens Lesson mode on a lesson.
  const initialStandardCode = urlParams.get("standard");
  // Bounded by the classroom's distributable group count (total − "other" pins),
  // passed in by the dashboard. Fall back to 10 only when absent (direct nav);
  // a legitimate 0 (all groups pinned) must be respected, not defaulted.
  const totalAvailableParam = parseInt(urlParams.get("totalAvailable"), 10);
  const totalAvailable = Number.isNaN(totalAvailableParam) ? 10 : totalAvailableParam;

  const {
    curriculumId,
    curriculumKind,
    gradeLevels,
    loaded: educatorLoaded,
    activeSchool,
  } = useEducatorAccount();
  // A state-framework school (its active school names a framework id) must
  // surface zero-mapped standards so the teacher can pick them and read the
  // zero-count warning; a Common Core school keeps the existing hidden rule.
  const showZeroCountStandards = standardsFrameworkId(activeSchool) !== null;
  // A new teacher's create flow defaults both grade pickers to their own grade
  // (lowest, if they hold several); null when unknown so we keep today's default.
  const teacherGrade = defaultTeacherGrade(gradeLevels);

  const [mode, setMode] = useState(initialMode === "standard" ? "standard" : "lesson");
  const switchMode = (next) => {
    if (next !== mode) trackSidebar(SIDEBAR_BUTTONS.MODE_TOGGLE, { mode: SIDEBAR_MODE_LABELS[next] });
    setMode(next);
  };
  const [selectedCourseId, setSelectedCourseId] = useState(initialCourseId || null);
  const [selectedLessonId, setSelectedLessonId] = useState(initialLessonId || null);
  const [expandedUnits, setExpandedUnits] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [isDistributionModalOpen, setIsDistributionModalOpen] = useState(false);
  const [error, setError] = useState(null);

  // --- Advanced Selection (question filters) ---
  // Per (track_type, standard_code) filter state, keyed `${trackType}|${code ?? ""}`,
  // mirroring the assign payload so "apply on assign" is a near-passthrough and
  // "clear on exit" is just a reset. Filters are cleared whenever the lesson or mode
  // changes (and naturally on unmount).
  // The question filters show on arrival, the way the edit view already does, so
  // there is no "Filter Questions" button to press and nothing to reopen when the
  // reset effect below clears the filters on a lesson change.
  const [filters, setFilters] = useState({});
  useEffect(() => {
    setFilters({});
  }, [selectedLessonId, mode]);

  // End-of-page Back to Top CTA — only when the content actually overflows the
  // scroll area. Re-measured after every render (content height only changes via
  // renders here) and on window resizes.
  const scrollContentRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const el = scrollContentRef.current;
    const check = () => setShowBackToTop(!!el && el.scrollHeight > el.clientHeight);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  });

  const filterKeyOf = (trackType, standardCode) => `${trackType}|${standardCode || ""}`;
  const getFilter = useCallback(
    (trackType, standardCode) => filters[`${trackType}|${standardCode || ""}`] || EMPTY_QUESTION_FILTER,
    [filters],
  );
  const setFilter = useCallback((trackType, standardCode, next) => {
    setFilters((prev) => ({ ...prev, [`${trackType}|${standardCode || ""}`]: next }));
  }, []);
  const coreFilter = filters[filterKeyOf("core", null)] || EMPTY_QUESTION_FILTER;
  // Lesson mode writes here through setFilter; standard mode through
  // StandardDetailView's onQuestionFiltersChange — so one derive serves both.
  const assignQuestionFilters = useMemo(() => toTrackQuestionFilters(filters), [filters]);
  // Query-shaped subset (drops the auto-assign flag, which isn't a question filter).
  const coreQueryFilter = {
    standards: coreFilter.standards,
    learningComponents: coreFilter.learningComponents,
    questionType: coreFilter.questionType,
  };

  // Standard-mode state — mirrors ViewChangeStandard's embedded standard picker.
  // Selection lives in local state (no URL round-trip) so it never collides with
  // the lesson params (courseId / lessonId / classroomId) on this route.
  //
  // The standards-with-counts fetch is the slowest call in the app and is only
  // rendered in Standard mode, so gate it behind the current mode. To keep the
  // first toggle to Standard mode instant on a cold cache, warm it in the
  // background once the browser is idle (after the critical lesson content has
  // loaded) — see the idle preload below.
  const [preloadStandards, setPreloadStandards] = useState(false);
  const standardsData = useStandards(true, mode === "standard");
  usePreloadStandards(true, preloadStandards);

  useEffect(() => {
    const scheduleIdle =
      window.requestIdleCallback || ((cb) => window.setTimeout(cb, 200));
    const cancelIdle = window.cancelIdleCallback || window.clearTimeout;
    const idleId = scheduleIdle(() => setPreloadStandards(true));
    return () => cancelIdle(idleId);
  }, []);

  const [selectedStandard, setSelectedStandard] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedGrade, setSelectedGrade] = useState(initialGrade || "all");
  const [selectedRelationships, setSelectedRelationships] = useState(null);

  // Open on the standard passed in the URL — selected, grade filtered and the
  // tree expanded to it. Applied once, as soon as the standards land, so it
  // never overrides a later manual pick. Same restore as CreateQuickGroup.
  const restoredInitialStandard = useRef(false);
  useEffect(() => {
    // Wait for the account too: the framework id comes from the active school,
    // and a restore applied before it lands would filter the wrong rows.
    if (!initialStandardCode || restoredInitialStandard.current || !educatorLoaded) return;
    const found = findSelectionByCode(standardsData, initialStandardCode, showZeroCountStandards);
    if (!found) return;
    restoredInitialStandard.current = true;
    setSelectedStandard({ ...found.entity, grade: found.grade, domain: found.domain, cluster: found.cluster });
    setSelectedGrade(found.grade);
    setExpandedNodes((prev) => ({
      ...prev,
      [found.grade]: true,
      [found.domain.code]: true,
      ...(found.cluster ? { [found.cluster.code]: true } : {}),
    }));
  }, [initialStandardCode, standardsData, showZeroCountStandards, educatorLoaded]);
  const {
    query: standardSearchQuery,
    results: standardSearchResults,
    showResults: showStandardSearchResults,
    handleInputChange: handleStandardSearchInputChange,
    handleSubmit: handleStandardSearchSubmit,
    clearSearch: clearStandardSearch,
    inputRef: standardSearchInputRef,
  } = useSemanticStandardSearch();
  const {
    query: lessonSearchQuery,
    results: lessonSearchResults,
    showResults: showLessonSearchResults,
    handleInputChange: handleLessonSearchInputChange,
    handleSubmit: handleLessonSearchSubmit,
    clearSearch: clearLessonSearch,
    inputRef: lessonSearchInputRef,
  } = useSemanticLessonSearch();

  const { courses: rawCourses, loading: coursesLoading } = useCurriculumCourses(curriculumId);
  // Curriculum order, not the API's: grades, then Accelerated 6/7, Algebra 1/2,
  // Geometry and the Integrated Maths. Anything unrecognised keeps its relative
  // position at the end (e.g. "Extra Support Materials for Algebra 1").
  const courses = useMemo(() => sortCoursesBySequence(rawCourses), [rawCourses]);
  // The default course: a grade passed in the URL (the last-worked bundle's
  // lesson grade) wins; otherwise the teacher's-grade course, else the first.
  // Courses are per-grade, and the bundle carries no course id — so grade is how
  // we resolve the lesson's course.
  const pickerGrade = initialGrade || teacherGrade;
  const defaultCourse =
    (pickerGrade && courses.find((c) => courseMatchesGrade(c.grade_level, pickerGrade))) || courses[0];
  // Warm the default course's units the moment courses land, before the
  // selectedCourseId state round-trip below enables useCurriculumUnits — so that
  // hook reads a warm (or in-flight, deduped) cache entry one render-tick sooner.
  usePrefetchCurriculumUnits(!selectedCourseId ? defaultCourse?.id : null);
  const { units, loading: unitsLoading } = useCurriculumUnits(selectedCourseId);
  const { lesson, loading: lessonLoading } = useLessonDetail(selectedLessonId);
  // Core carousel reflects the Core filter (no-op filter === the unfiltered call).
  const { questions, total: questionsTotal, loading: questionsLoading, loadMore: loadMoreQuestions } =
    useLessonQuestions(selectedLessonId, coreQueryFilter);
  // Cascading facet availability for the Core filter panel — lesson mode only
  // (standard mode fetches its own). Drives dropdown enable/disable + live counts.
  const {
    facets: coreFacets,
    hasLoaded: coreFacetsLoaded,
    error: coreFacetsError,
  } = useQuestionFacets(
    selectedLessonId,
    { standardCode: null, ...coreQueryFilter },
    { enabled: mode === "lesson" },
  );

  useEffect(() => {
    if (!defaultCourse || selectedCourseId || !educatorLoaded) return;
    setSelectedCourseId(defaultCourse.id);
  }, [defaultCourse, selectedCourseId, educatorLoaded]);

  // Available grades: skip grades with no challenge-bearing standards (a state
  // framework keeps grades whose only standards are zero-mapped).
  const availableGrades = useMemo(
    () =>
      (standardsData || [])
        .filter((gradeLevel) => gradeHasVisibleStandards(gradeLevel, showZeroCountStandards))
        .map((gradeLevel) => gradeLevel.grade),
    [standardsData, showZeroCountStandards],
  );

  // Standard-mode grade dropdown: default to the teacher's grade on first open —
  // unless a grade was passed in the URL (last-worked bundle), which wins. Checked
  // against the options, so a teacher whose grade has no standards keeps All Grades
  // rather than a selection the dropdown can't show.
  useTeacherGradeDefault(gradeLevels, educatorLoaded && !initialGrade, availableGrades, setSelectedGrade);

  useEffect(() => {
    if (selectedLessonId && units.length > 0) {
      for (const unit of units) {
        if (unit.lessons?.some((l) => l.id === selectedLessonId)) {
          setExpandedUnits((prev) => ({ ...prev, [unit.id]: true }));
          const matchingModule = unit.modules?.find((m) =>
            m.lessons?.some((l) => l.id === selectedLessonId)
          );
          if (matchingModule) {
            setExpandedModules((prev) => ({ ...prev, [matchingModule.id]: true }));
          }
          break;
        }
      }
    }
  }, [selectedLessonId, units]);

  useEffect(() => {
    if (selectedLessonId || units.length === 0) return;
    const firstUnitWithLesson = units.find((u) => u.lessons?.length > 0);
    if (!firstUnitWithLesson) return;
    const firstLesson = firstUnitWithLesson.lessons[0];
    setSelectedLessonId(firstLesson.id);
    setExpandedUnits((prev) => ({ ...prev, [firstUnitWithLesson.id]: true }));
    const matchingModule = firstUnitWithLesson.modules?.find((m) =>
      m.lessons?.some((l) => l.id === firstLesson.id)
    );
    if (matchingModule) {
      setExpandedModules((prev) => ({ ...prev, [matchingModule.id]: true }));
    }
  }, [units, selectedLessonId]);

  // Both sidebar paths clear the search, like handleSelectStandard: the content
  // pane renders results whenever showLessonSearchResults is set, regardless of
  // the selection, so a tree click or a course switch mid-search would update
  // state under a results view that never goes away — the click reads as dead.
  const handleCourseChange = (e) => {
    const course = courses.find((entry) => entry.id === e.target.value);
    trackSidebar(SIDEBAR_BUTTONS.COURSE_FILTER, { course_id: e.target.value, course_name: course?.name });
    setSelectedCourseId(e.target.value);
    setSelectedLessonId(null);
    setExpandedUnits({});
    setExpandedModules({});
    if (showLessonSearchResults) clearLessonSearch();
  };

  const handleToggleUnit = useCallback((unitId) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
  }, []);

  const handleToggleModule = useCallback((moduleId) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  }, []);

  const handleSelectLesson = useCallback((lessonId) => {
    setSelectedLessonId(lessonId);
    if (showLessonSearchResults) clearLessonSearch();
  }, [showLessonSearchResults, clearLessonSearch]);

  // A search result can live in a course other than the one on screen, so the
  // course and the lesson are set in the SAME update. Split across two ticks,
  // the new course's units land with selectedLessonId still null and the
  // auto-select effect above claims the first lesson of the first unit instead.
  const handleSelectLessonResult = useCallback((result) => {
    setSelectedCourseId(result.course_id);
    setSelectedLessonId(result.lesson_id);
    setExpandedUnits({ [result.unit_id]: true });
    if (result.module_id) {
      setExpandedModules({ [result.module_id]: true });
    } else {
      setExpandedModules({});
    }
    clearLessonSearch();
  }, [clearLessonSearch]);

  const handleAssign = (lessonId) => {
    if (!classroomId) {
      setError("No classroom selected. Go back to Groups and select a class first.");
      return;
    }
    trackClickedButton("Assign Lesson", {
      page_name: "Lessons & Standards",
      assignment_type: "Lesson",
      lesson_id: lessonId || null,
      class: classroomId || null,
      course_id: initialCourseId || null,
    });
    setError(null);
    setIsDistributionModalOpen(true);
  };

  const coreStandard = lesson?.addressing?.[0] || null;
  const selectedCourse = courses.find((c) => c.id === selectedCourseId);
  const lessonInfoSubtitle = lesson
    ? [selectedCourse?.name, lesson.unit_ordinal_name, lesson.ordinal_name].filter(Boolean).join(", ")
    : null;

  // --- Standard-mode helpers (active when mode === "standard") ---
  // Fetch the picked standard's relationships so the distribution modal knows
  // whether prerequisite / extension tracks are available.
  useEffect(() => {
    if (!selectedStandard?.code || selectedStandard.type) {
      setSelectedRelationships(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await request(`standards/${selectedStandard.code}/relationships`, { method: "GET" });
        if (!cancelled) setSelectedRelationships(data);
      } catch (e) {
        if (!cancelled) setSelectedRelationships(null);
        logError("LessonsStandards: failed to load standard relationships", e, {
          category: LogCategory.ERROR,
          standardCode: selectedStandard.code,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStandard?.code, selectedStandard?.type]);

  const handleToggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const handleSelectStandard = (entity, grade, domain, cluster) => {
    setSelectedStandard({ ...entity, grade, domain, cluster });
    // A search result can be from a grade the dropdown filter is hiding — follow
    // it, the way picking a lesson result switches the course. Tree clicks pass
    // the on-screen grade, so this only fires for cross-grade selections.
    if (grade && selectedGrade !== "all" && grade !== selectedGrade) {
      setSelectedGrade(grade);
    }
    // Selecting opens the node and its path, the way picking a lesson opens its
    // unit. Without this a teacher who clicks a domain sees it selected but still
    // collapsed, and has to click the chevron to reach what is inside it.
    setExpandedNodes((prev) => ({
      ...prev,
      ...(grade ? { [grade]: true } : {}),
      ...(domain?.code ? { [domain.code]: true } : {}),
      ...(cluster?.code ? { [cluster.code]: true } : {}),
      ...(entity?.code ? { [entity.code]: true } : {}),
    }));
    if (showStandardSearchResults) clearStandardSearch();
  };

  const handleGradeChange = (e) => {
    const grade = e.target.value;
    trackSidebar(SIDEBAR_BUTTONS.GRADE_FILTER, { grade: sidebarGradeValue(grade) });
    setSelectedGrade(grade);
    if (grade !== "all") {
      setExpandedNodes((prev) => ({ ...prev, [grade]: true }));
    }
  };

  const filteredStandards = selectedGrade === "all"
    ? (standardsData || [])
    : (standardsData || []).filter((g) => g.grade === selectedGrade);

  // Only leaf standards (not grade / domain / cluster) carry relationships.
  const isLeafStandard = selectedStandard && !selectedStandard.type;
  // Clusters and domains are valid assign targets too, but Core-only — they have no
  // prereq / extension tracks. Grades stay unassignable. The track gating below
  // still keys on isLeafStandard, which is the separate "carries relationships"
  // question.
  const canAssignSelection = isAssignableTarget(selectedStandard);
  const standardHasPrereqs = (selectedRelationships?.prerequisites || []).some(
    (group) => group.standards && group.standards.length > 0
  );
  const standardHasExtensions = (selectedRelationships?.extensions || []).some(
    (group) => group.standards && group.standards.length > 0
  );

  // Tracks whose backing standard(s) have no questions to assign — disabled in the
  // distribution modal so the educator can't send groups to an empty level. Computed
  // from the same question call each flow's detail panel uses (not the global tree
  // count, which disagrees with lesson-scoped availability).
  const noQuestionTracks = useNoQuestionTracks(
    mode === "standard"
      ? {
        flow: "standard",
        coreCodes: selectedStandard?.code ? [selectedStandard.code] : [],
        prereqCodes: (selectedRelationships?.prerequisites || []).flatMap((g) => (g.standards || []).map((s) => s.code)),
        extCodes: (selectedRelationships?.extensions || []).flatMap((g) => (g.standards || []).map((s) => s.code)),
      }
      : {
        flow: "lesson",
        lessonId: selectedLessonId,
        coreCodes: (lesson?.addressing || []).map((a) => a.standard_code).filter(Boolean),
        prereqCodes: lessonPrereqCodes(lesson),
        extCodes: buildingTowardOrTier1(lesson).map((b) => b.standard_code).filter(Boolean),
      },
  );

  const handleAssignStandard = () => {
    if (!classroomId) {
      setError("No classroom selected. Go back to Groups and select a class first.");
      return;
    }
    if (!canAssignSelection) {
      setError("Select a specific standard or cluster (not a grade or domain) to assign.");
      return;
    }
    trackClickedButton("Assign Standard", {
      page_name: "Lessons & Standards",
      assignment_type: "Standard",
      standard_code: selectedStandard?.code || null,
      class: classroomId || null,
    });
    setError(null);
    setIsDistributionModalOpen(true);
  };

  const handleBack = () => {
    history.push(constants.EDUCATOR_PATH);
  };
  // Only the sidebar's own button is a sidebar click; the footers share
  // handleBack and stay untracked, as on the other pages with this sidebar.
  const handleSidebarBack = () => {
    trackSidebar(SIDEBAR_BUTTONS.BACK_TO_GROUPS);
    handleBack();
  };

  const { activeModule, activeUnitOrdinal } = useMemo(() => {
    if (!units || !selectedLessonId) return { activeModule: null, activeUnitOrdinal: null };
    for (const unit of units) {
      if (unit.modules) {
        const found = unit.modules.find((m) =>
          m.lessons?.some((l) => l.id === selectedLessonId)
        );
        if (found) return { activeModule: found, activeUnitOrdinal: unit.ordinal_name };
      }
    }
    return { activeModule: null, activeUnitOrdinal: null };
  }, [units, selectedLessonId]);

  if (educatorLoaded && curriculumKind === "CCSS_DIFFERENTIATION") {
    history.push(constants.GROUP_CODE_STANDARD_SEARCH_PATH.replace(":groupCode", "search"));
    return null;
  }

  if (educatorLoaded && !curriculumId) {
    return (
      <div className="lessons-standards">
        <div className="ls-sidebar ls-sidebar--picker">
          <div className="ls-sidebar-header">
            <img src={logo} alt="OKO" className="ls-sidebar-logo" />
            <button className="ls-back-button" onClick={handleSidebarBack}>
              <img src={arrow_left_circle} alt="back" style={{ width: 20 }} />
              Back to Groups
            </button>
          </div>
        </div>
        <div className="ls-main-content">
          <div className="ls-content-header">
            <h1 className="ls-header-title">Create Groups</h1>
          </div>
          <div className="ls-no-selection">
            Your school does not have a curriculum assigned. Contact your OKO admin to enable the lesson picker.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lessons-standards">
      <div className="ls-sidebar ls-sidebar--picker">
        <div className="ls-sidebar-header">
          <img src={logo} alt="OKO" className="ls-sidebar-logo" />
          <button className="ls-back-button" onClick={handleSidebarBack}>
            <img src={arrow_left_circle} alt="back" style={{ width: 20 }} />
            Back to Groups
          </button>
          <div className="ls-toggle-container">
            <button
              className={`ls-toggle-button ${mode === "lesson" ? "active" : ""}`}
              onClick={() => switchMode("lesson")}
            >
              Lesson
            </button>
            <button
              className={`ls-toggle-button ${mode === "standard" ? "active" : ""}`}
              onClick={() => switchMode("standard")}
            >
              Standard
            </button>
          </div>
          {mode === "standard" ? (
            <select
              className="ls-grade-dropdown"
              aria-label="Grade"
              value={selectedGrade}
              onChange={handleGradeChange}
            >
              <option value="all">All Grades</option>
              {availableGrades.map((grade) => (
                <option key={grade} value={grade}>
                  {getGradeLabel(grade)}
                </option>
              ))}
            </select>
          ) : (
            <select
              className="ls-grade-dropdown"
              aria-label="Grade"
              value={selectedCourseId || ""}
              onChange={handleCourseChange}
              disabled={coursesLoading}
            >
              {coursesLoading && <option>Loading...</option>}
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="ls-sidebar-content">
          {mode === "standard" ? (
            !standardsData || standardsData.length === 0 ? (
              <div className="ls-loading">
                <StandardsFallback />
              </div>
            ) : (
              <StandardNavigationTree
                standards={filteredStandards}
                selectedStandard={selectedStandard}
                expandedNodes={expandedNodes}
                onToggleNode={handleToggleNode}
                onSelectStandard={handleSelectStandard}
                showZeroCountStandards={showZeroCountStandards}
              />
            )
          ) : unitsLoading ? (
            <div className="ls-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          ) : (
            <LessonSidebarTree
              units={units}
              selectedLessonId={selectedLessonId}
              onSelectLesson={handleSelectLesson}
              expandedUnits={expandedUnits}
              onToggleUnit={handleToggleUnit}
              expandedModules={expandedModules}
              onToggleModule={handleToggleModule}
            />
          )}
        </div>
      </div>

      <div className="ls-main-content">
        <div className="ls-content-header">
          <h1 className="ls-header-title">{mode === "standard" ? "Create Groups" : "Create Lesson Groups"}</h1>
          <div className="ls-header-search">
            <span className="ls-header-search-icon">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            {mode === "standard" ? (
              <input
                ref={standardSearchInputRef}
                className="ls-header-search-input"
                placeholder={'Try: "Multiplying Fractions" or "Proportional relationships"'}
                value={standardSearchQuery}
                onChange={handleStandardSearchInputChange}
                onKeyDown={(e) => { if (e.key === "Enter") handleStandardSearchSubmit(); }}
              />
            ) : (
              <input
                ref={lessonSearchInputRef}
                className="ls-header-search-input"
                placeholder={'Try: "Multiplying Fractions" or "Proportional relationships"'}
                value={lessonSearchQuery}
                onChange={handleLessonSearchInputChange}
                onKeyDown={(e) => { if (e.key === "Enter") handleLessonSearchSubmit(); }}
              />
            )}
          </div>
        </div>

        <div className="ls-scrollable-content" ref={scrollContentRef}>
          {mode === "standard" ? (
            !standardsData || standardsData.length === 0 ? (
              <div className="ls-loading">
                <StandardsFallback size="2x" />
              </div>
            ) : showStandardSearchResults ? (
              <SearchResultsView
                searchResults={standardSearchResults || []}
                onResultClick={handleSelectStandard}
                onBackClick={clearStandardSearch}
                standardsData={standardsData}
                showZeroCountStandards={showZeroCountStandards}
                activeSchool={activeSchool}
              />
            ) : selectedStandard ? (
              <StandardDetailView
                standard={selectedStandard}
                groupCode={null}
                onStandardClick={handleSelectStandard}
                pickerMode
                onQuestionFiltersChange={setFilters}
                showZeroCountStandards={showZeroCountStandards}
              />
            ) : (
              <div className="ls-no-selection">
                Select a standard from the sidebar to view details.
              </div>
            )
          ) : showLessonSearchResults ? (
            <LessonSearchResultsView
              searchResults={lessonSearchResults || []}
              onResultClick={handleSelectLessonResult}
              onBackClick={clearLessonSearch}
            />
          ) : lessonLoading ? (
            <div className="ls-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          ) : lesson ? (
            <LessonDetailPanel
              lesson={lesson}
              module={activeModule}
              unitOrdinal={activeUnitOrdinal}
              questions={questions}
              questionsLoading={questionsLoading}
              questionsTotal={questionsTotal}
              onLoadMoreQuestions={loadMoreQuestions}
              onAssign={handleAssign}
              onBack={handleBack}
              showAssignButton={!!classroomId}
              enableQuestionFilter
              filterOpen
              bankTitle={BANK_TITLE}
              bankSubtitle={BANK_SUBTITLE}
              showAutoAssign={false}
              standardsSectionVariant="v3"
              getFilter={getFilter}
              setFilter={setFilter}
              coreFacets={coreFacets}
              coreFacetsLoaded={coreFacetsLoaded}
              coreFacetsError={coreFacetsError}
            />
          ) : (
            <div className="ls-no-selection">
              Select a lesson from the sidebar to view details.
            </div>
          )}
          {showBackToTop && (
            <button
              type="button"
              className="ls-back-to-top"
              onClick={() => scrollContentRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <BackToTopIcon />
              Back to Top
            </button>
          )}
        </div>

        {mode === "standard" && classroomId && (
          <div className="ls-assign-bar">
            <button className="ls-save-secondary" onClick={handleBack}>
              Back to Groups
            </button>
            <button
              className="ls-save-primary"
              onClick={handleAssignStandard}
              disabled={!canAssignSelection}
              title={!canAssignSelection ? "Select a specific standard or cluster (not a grade or domain) to create groups" : ""}
            >
              Create Groups
            </button>
          </div>
        )}
      </div>

      <AssignGroupsDistributionModal
        isOpen={isDistributionModalOpen}
        onClose={() => setIsDistributionModalOpen(false)}
        classroomId={classroomId}
        standardCode={mode === "standard"
          ? (selectedStandard?.code || null)
          : (coreStandard?.standard_code || null)}
        standardName={mode === "standard"
          ? (selectedStandard?.name || null)
          : (lesson?.name || coreStandard?.standard_name || null)}
        infoSubtitle={mode === "standard" ? null : lessonInfoSubtitle}
        infoTitle={mode === "standard" ? (selectedStandard?.name || null) : (lesson?.name || null)}
        totalAvailable={totalAvailable}
        // Standard mode: the selected standard is itself the core. Lesson mode:
        // only offer the Core track when the lesson has a core (addressing)
        // standard, so the modal never offers an unassignable core.
        hasCore={mode === "standard" ? true : (lesson?.addressing?.length || 0) > 0}
        // A cluster target is Core-only — no prereq / extension tracks exist for it.
        hasPrerequisites={mode === "standard"
          ? Boolean(isLeafStandard && standardHasPrereqs)
          : lessonPrereqCodes(lesson).length > 0}
        hasExtensions={mode === "standard"
          ? Boolean(isLeafStandard && standardHasExtensions)
          : buildingTowardOrTier1(lesson).length > 0}
        // Disable (grey out) any track whose backing standard(s) have no
        // questions to assign, so the educator can't send groups to an empty level.
        noQuestionTracks={noQuestionTracks}
        // The Advanced Selection filters the teacher applied while picking are
        // carried into creation, so the new groups start on the narrowed bank
        // instead of the full one. The backend matches each entry to a created
        // group by (track_type, standard_code) and ignores the rest.
        questionFilters={assignQuestionFilters}
        // Lesson mode: assign the lesson (auto-distributes server-side, and
        // honors the chosen distribution once the BE supports it). Standard
        // mode leaves onSubmit undefined so the modal falls back to its
        // default POST /groups/assign, which sends the same filters.
        onSubmit={mode === "lesson"
          ? (counts) => assignLesson(classroomId, selectedLessonId, counts, assignQuestionFilters)
          : undefined}
        // Lesson mode: source the clearable pool from the unified read and route
        // clears by bundle_type so lesson-owned codes are freed via the curriculum
        // API (not the groups/ path, which 400s on cross-model codes).
        useUnifiedPool={mode === "lesson"}
        onSaved={() => {
          setIsDistributionModalOpen(false);
          history.push(`${constants.EDUCATOR_PATH}?classId=${classroomId}`);
        }}
      />

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          style={{ position: "fixed", bottom: 16, right: 16, zIndex: 9999, maxWidth: 400 }}
        >
          {error}
        </Alert>
      )}
    </div>
  );
};

export const LessonsStandards = withPanelViewMemory(LessonsStandardsPage);
